import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Plus, X, Paperclip, Send, Zap, User } from 'lucide-react';
import apiService from '@/services/api';

interface Message {
  id: string;
  type: 'system' | 'provider' | 'admin';
  author: string;
  role?: string;
  time: string;
  content: string;
  avatar?: string;
}

const AdminCaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState('');
  const [priorityLevel, setPriorityLevel] = useState('High (Level 2)');
  const [statusTracker, setStatusTracker] = useState('In Progress');
  const [messages, setMessages] = useState<Message[]>([]);
  const [caseData, setCaseData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch case and messages from database
  useEffect(() => {
    const fetchCaseData = async () => {
      try {
        const [caseResponse, messagesResponse] = await Promise.all([
          apiService.request(`/admin/support-cases/${caseId}`, 'GET'),
          apiService.request(`/admin/support-cases/${caseId}/messages`, 'GET')
        ]);

        if (caseResponse.success && caseResponse.data) {
          setCaseData(caseResponse.data);
          setPriorityLevel(caseResponse.data.priority || 'High (Level 2)');
          setStatusTracker(caseResponse.data.status || 'In Progress');
        }

        if (messagesResponse.success && messagesResponse.data) {
          // (audit 2026-06-11) — backend returns
          // { id, case_id, message_text, sender_type, sender_id, created_at }
          // but the JSX renders { content, type, role, time, author, avatar }.
          // Map field names here so the thread actually shows the text.
          const mapped: Message[] = (messagesResponse.data as any[]).map((m) => {
            const sender = String(m.sender_type || m.type || 'admin').toLowerCase();
            const type: Message['type'] =
              sender === 'system' ? 'system'
                : sender === 'provider' ? 'provider'
                : sender === 'customer' ? 'admin' // render customer like admin (light card)
                : 'admin';
            const role =
              sender === 'system' ? 'PLATFORM'
                : sender === 'provider' ? 'PROVIDER'
                : sender === 'customer' ? 'CUSTOMER'
                : 'ADMIN';
            const ts = m.created_at || m.time;
            return {
              id: String(m.id),
              type,
              role,
              author: m.author || m.sender_name || (sender === 'admin' ? 'Admin' : sender.charAt(0).toUpperCase() + sender.slice(1)),
              time: ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '',
              content: m.message_text || m.content || '',
              avatar: (m.author || m.sender_name || sender).slice(0, 2).toUpperCase(),
            };
          });
          setMessages(mapped);
        }
      } catch (error) {
        console.error('Failed to fetch case data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchCaseData();
    }
  }, [caseId]);

  const displayMessages = messages;

  // Handler: Send message to case thread
  const handleSendMessage = async () => {
    if (!replyText.trim()) return;
    try {
      const response = await apiService.request(`/admin/support-cases/${caseId}/messages`, 'POST', {
        content: replyText,
        messageType: 'admin',
        attachments: [],
        sentBy: localStorage.getItem('adminId'),
        sentAt: new Date().toISOString()
      });
      if (response.success) {
        setReplyText('');
        alert('✓ Message sent successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Send message error:', error);
      alert('Failed to send message: ' + error.message);
    }
  };

  // Handler: Resolve support case
  const handleResolveCase = async () => {
    try {
      const response = await apiService.request(`/admin/support-cases/${caseId}/resolve`, 'PATCH', {
        status: 'RESOLVED',
        resolution: 'Case resolved by admin',
        resolvedBy: localStorage.getItem('adminId'),
        resolvedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Case resolved successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Resolve case error:', error);
      alert('Failed to resolve case: ' + error.message);
    }
  };

  // Handler: Escalate case to manager
  const handleEscalateCase = async () => {
    try {
      const response = await apiService.request(`/admin/support-cases/${caseId}/escalate`, 'POST', {
        escalationLevel: 'MANAGER',
        reason: 'Escalated by admin',
        escalatedBy: localStorage.getItem('adminId'),
        escalatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Case escalated to manager');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Escalate case error:', error);
      alert('Failed to escalate case: ' + error.message);
    }
  };

  // Handler: Update case priority level
  const handleUpdatePriority = async (newPriority: string) => {
    try {
      const response = await apiService.request(`/admin/support-cases/${caseId}/update-priority`, 'PATCH', {
        priority: newPriority,
        updatedBy: localStorage.getItem('adminId'),
        updatedAt: new Date().toISOString()
      });
      if (response.success) {
        setPriorityLevel(newPriority);
        alert('✓ Priority updated successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Update priority error:', error);
      alert('Failed to update priority: ' + error.message);
    }
  };

  // Handler: Update case status
  const handleUpdateStatus = async (newStatus: string) => {
    try {
      const response = await apiService.request(`/admin/support-cases/${caseId}/update-status`, 'PATCH', {
        status: newStatus,
        updatedBy: localStorage.getItem('adminId'),
        updatedAt: new Date().toISOString()
      });
      if (response.success) {
        setStatusTracker(newStatus);
        alert('✓ Status updated successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Update status error:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Case Thread - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          <div className="grid grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="col-span-2">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Case Thread</h1>
                  <p className="text-sm text-gray-600 mt-2">ID: <span className="font-mono font-bold text-gray-900">#{caseId}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Print">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 12a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate('/admin/support-disputes')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Messages Thread */}
              <div className="space-y-6 mb-8">
                {displayMessages.map((msg, idx) => (
                  <div key={msg.id} className="flex gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {msg.type === 'system' ? (
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-sm">
                          <Zap className="w-6 h-6 text-white" />
                        </div>
                      ) : msg.type === 'provider' ? (
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm">
                          {msg.avatar}
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center shadow-sm">
                          <User className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Message Content */}
                    <div className="flex-1">
                      <div className={`rounded-xl p-5 border ${
                        msg.type === 'system'
                          ? 'bg-teal-50 border-teal-200'
                          : msg.type === 'provider'
                          ? 'bg-teal-700 border-teal-700 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}>
                        <p className={`text-sm leading-relaxed font-medium ${msg.type === 'provider' ? 'text-white' : ''}`}>
                          {msg.content}
                        </p>
                      </div>
                      <div className="mt-3 flex items-center gap-3">
                        <span className={`text-xs font-bold uppercase tracking-wider ${
                          msg.type === 'system'
                            ? 'text-teal-700'
                            : msg.type === 'provider'
                            ? 'text-purple-700'
                            : 'text-gray-700'
                        }`}>
                          {msg.role}
                        </span>
                        <span className="text-xs text-gray-500">–</span>
                        <span className="text-xs text-gray-500">{msg.time}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <label className="text-xs font-bold text-gray-700 uppercase block mb-3">Reply to Provider</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your response here..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                  rows={5}
                />
                <div className="flex items-center justify-between mt-5">
                  <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm">
                    <Paperclip className="w-4 h-4" />
                    <span>Attach File</span>
                  </button>
                  <Button onClick={handleSendMessage} className="bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center gap-2 shadow-sm">
                    <Send className="w-4 h-4" />
                    Send Message
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Case Intelligence */}
            <div className="col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-5">
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">📊 Case Intelligence</h3>
                </div>

                <div className="p-6 space-y-6">
                  {/* Related Job ID */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg">📁</span>
                      </div>
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Related Job</span>
                    </div>
                    <p className="text-xl font-bold text-teal-600 mb-4">
                      {caseData?.booking_id ? `#JOB-${caseData.booking_id}` : caseData?.job_reference || caseData?.job_id ? `#JOB-${caseData.job_id}` : '—'}
                    </p>
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-600 uppercase">Service</span>
                        <span className="font-bold text-gray-900">{caseData?.service_type || caseData?.category || '—'}</span>
                      </div>
                      <div className="border-t border-gray-200"></div>
                      <div className="flex justify-between items-center pt-2">
                        <span className="text-xs font-semibold text-gray-600 uppercase">Job Value</span>
                        <span className="font-bold text-lg text-gray-900">
                          {caseData?.job_value != null ? `$${Number(caseData.job_value).toFixed(2)}` : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* SLA Deadline */}
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-300 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs font-bold text-orange-900 uppercase tracking-wider">SLA Deadline</span>
                    </div>
                    <p className="text-3xl font-bold text-orange-600 mb-2">
                      {caseData?.sla_deadline ? new Date(caseData.sla_deadline).toLocaleTimeString('en-GB') : '—'}
                    </p>
                    <p className="text-xs text-orange-800 font-medium">
                      {caseData?.sla_deadline ? `Deadline: ${new Date(caseData.sla_deadline).toLocaleString('en-GB')}` : 'SLA pending'}
                    </p>
                  </div>

                  {/* Priority Level */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase block mb-3">Priority Level</label>
                    <select
                      value={priorityLevel}
                      onChange={(e) => handleUpdatePriority(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white"
                    >
                      <option>Low (Level 1)</option>
                      <option>High (Level 2)</option>
                      <option>Critical (Level 3)</option>
                    </select>
                  </div>

                  {/* Status Tracker */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase block mb-3">Status Tracker</label>
                    <select
                      value={statusTracker}
                      onChange={(e) => handleUpdateStatus(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white"
                    >
                      <option>Open</option>
                      <option>In Progress</option>
                      <option>Resolved</option>
                      <option>Escalated</option>
                    </select>
                  </div>

                  {/* Command Actions */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">⚡ Command Actions</h4>
                    <div className="space-y-3">
                      <Button
                        onClick={handleResolveCase}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 py-2.5 rounded-lg shadow-sm transition-all"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Resolve Case
                      </Button>
                      <Button onClick={handleEscalateCase} className="w-full border-2 border-orange-400 bg-white hover:bg-orange-50 text-gray-900 font-bold flex items-center justify-center gap-2 py-2.5 rounded-lg transition-all">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        Escalate to Manager
                      </Button>
                      <button className="w-full border-2 border-teal-300 bg-white hover:bg-teal-50 rounded-lg py-2.5 flex items-center justify-center text-teal-600 font-semibold transition-all">
                        <Plus className="w-5 h-5" />
                      </button>
                      <p className="text-xs text-gray-600 text-center font-medium">Link to Dispute</p>
                    </div>
                  </div>

                  {/* Internal Note */}
                  <div className="border-t border-gray-200 pt-6 bg-blue-50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <p className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-1">Internal Note Enabled</p>
                        <p className="text-xs text-blue-700 leading-relaxed">All internal logs are private and not visible to the provider or customer</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminCaseDetailPage;
